import React, { useState, useEffect, useMemo, FC, useRef } from "react";
import styles from "./SelectedScreensPopup.module.scss";
import LimitModal from "./LimitModal";
import SavedGroupsPopup from "./SavedGroupsPopup";
import useAuthProtection from "@/Hooks/useAuthProtection";
import { useDispatch } from "react-redux";
import { useCurrentOrganization } from "@/Hooks/useCurrentOrganization";
import { useSelectedScreensStates } from "@/Hooks/selectedScreens/useSelectedScreensStates";
import { useSaveToGroupScreensHandlers } from "@/Hooks/selectedScreens/useSaveToGroupScreensHandlers";
import { useUnsaveScreensHandlers } from "@/Hooks/selectedScreens/useUnsaveScreensHandlers";
import { useSelectedScreensDownload } from "@/Hooks/selectedScreens/useSelectedScreensDownload";
import { saveModeActions } from "@/entities/saveMode";
import { useOpenProModal } from "@/Hooks/useOpenBillingModal";

const MAX_SCREENS = 30;

export type SelectedScreenType =
  | "appReplays"
  | "appStoreScreens"
  | "onboardingScreens"
  | "searchScreens"
  | "animationScreens";

interface SelectedScreensPopupProps {
  type: SelectedScreenType;
  setSearchResults?: React.Dispatch<React.SetStateAction<any[]>>;
  updateScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;
  updateReplayScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;

  setAnimations?: React.Dispatch<React.SetStateAction<any[]>>;
  setSimilarScreenResults?: React.Dispatch<React.SetStateAction<any[]>>;
  setAppStoreScreens?: React.Dispatch<React.SetStateAction<any[]>>;
}

const SelectedScreensPopup: FC<SelectedScreensPopupProps> = (props) => {
  const {
    type,
    setSearchResults,
    updateScreensSavedStatus,
    updateReplayScreensSavedStatus,
    setAnimations,
    setSimilarScreenResults,
    setAppStoreScreens,
  } = props;

  const organization = useCurrentOrganization();
  const { withAuth } = useAuthProtection();

  const dispatch = useDispatch();

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [savedPointIds, setSavedPointIds] = useState<number[]>([]);
  const [justSaved, setJustSaved] = useState(false); // Track if screens were just saved

  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const savePopupRef = useRef<HTMLDivElement>(null);

  const { handleDownload, isDownloading } = useSelectedScreensDownload({
    type,
  });

  const { handleSave, handleSaveToGroup, isSaveToGroupLoading } =
    useSaveToGroupScreensHandlers({
      type,
      setSearchResults,
      updateScreensSavedStatus,
      updateReplayScreensSavedStatus,
      setAnimations,
      setSimilarScreenResults,
      setShowSavePopup,
      setAppStoreScreens,
    });

  const { screens, clearScreens, removeSelectedScreen } =
    useSelectedScreensStates({ type });

  const { handleUnsave, isUnsaveLoading } = useUnsaveScreensHandlers({
    type,
    setSearchResults,
    updateScreensSavedStatus,
    updateReplayScreensSavedStatus,
    setAnimations,
    setSimilarScreenResults,
    setAppStoreScreens,
  });

  // Determine if any API operation is loading
  const isLoading = isSaveToGroupLoading || isUnsaveLoading;

  // Check if selected screens exceed limit
  useEffect(() => {
    if (screens.length > MAX_SCREENS) {
      setShowLimitModal(true);
    } else {
      setShowLimitModal(false);
    }
  }, [screens.length]);

  // Close save popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        saveButtonRef.current &&
        !saveButtonRef.current.contains(event.target as Node) &&
        savePopupRef.current &&
        !savePopupRef.current.contains(event.target as Node)
      ) {
        setShowSavePopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCloseModal = () => {
    if (screens.length > 0) {
      const lastScreenId = screens[screens.length - 1].id;
      removeSelectedScreen(lastScreenId);
    }

    setShowLimitModal(false);
  };

  // Determine save button state based on selected screens
  const { saveButtonText, saveButtonIcon, handleSaveAction, isSaveMode } =
    useMemo(() => {
      // Check if all screens are saved
      const allSaved =
        screens.length > 0 &&
        screens.every(
          (screen) => screen.isSaved || screen.is_saved || screen.is_saved_pk,
        );

      // Check if any screens are saved
      const anySaved = screens.some(
        (screen) => screen.isSaved || screen.is_saved || screen.is_saved_pk,
      );

      // Default state - all unsaved
      let buttonText = "Save";
      let buttonIcon = "/img/blackIcon/Bookmark.svg";
      let isSaveMode = true;

      if (allSaved || justSaved) {
        // Case 3: All screens are saved or just saved
        buttonText = "Unsave";
        buttonIcon = "/img/blackIcon/BookmarkUnsaved.svg";
        isSaveMode = false;
      } else if (anySaved) {
        // Case 2: Mix of saved and unsaved
        buttonText = "Save";
        buttonIcon = "/img/blackIcon/Bookmark.svg";
        isSaveMode = true;
      }

      const handleAction = withAuth(async () => {
        if (isSaveMode) {
          // Save screens
          const pointIds = await handleSave();
          if (pointIds) {
            setSavedPointIds(pointIds);
            setJustSaved(true); // Mark as just saved to force "Unsave" button
            setShowSavePopup(true); // Show groups popup
          }
        } else {
          // Unsave screens
          await handleUnsave();
          setJustSaved(false); // Reset the just saved flag
        }
      });

      return {
        saveButtonText: buttonText,
        saveButtonIcon: buttonIcon,
        handleSaveAction: handleAction,
        isSaveMode,
      };
    }, [
      screens,
      justSaved,
      type,
      withAuth,
      organization?.uuid,
      dispatch,
      setSearchResults,
      updateScreensSavedStatus,
      updateReplayScreensSavedStatus,
      setAnimations,
      setSimilarScreenResults,
      handleSave,
      handleUnsave,
    ]);

  const openProModal = useOpenProModal();

  // Wrap download handler with authentication check
  const handleDownloadClick = withAuth((e) => {
    e.stopPropagation();
    if (!organization?.is_pro) {
      // If user is authenticated but not Pro, show Pro modal
      openProModal("Selected Screens Popup");
    } else {
      // If user is authenticated and Pro, proceed with download
      handleDownload();
    }
  });

  return (
    <>
      <div className={styles.selectedScreensPopupContainer}>
        <div className={styles.selectedScreensPopupSelectedButton}>
          <p>{screens.length} Selected</p>
          <button
            onClick={clearScreens}
            className={styles.selectedScreensPopupSelectedButtonClose}
          >
            <img src="/img/whiteIcon/Close.svg" alt="Clear All" />
          </button>
        </div>
        <div className={styles.saveButtonWrapper}>
          <button
            ref={saveButtonRef}
            className={styles.selectedScreensPopupSaveButton}
            onClick={handleSaveAction}
            disabled={isLoading} // Disable button while loading
          >
            <img
              src={saveButtonIcon}
              alt={isSaveMode ? "Save Button Icon" : "Unsave Button Icon"}
            />
            <p>
              {isLoading
                ? isSaveMode
                  ? "Saving..."
                  : "Unsaving..."
                : saveButtonText}
            </p>
          </button>

          {showSavePopup && (
            <SavedGroupsPopup
              ref={savePopupRef}
              organizationId={organization?.uuid}
              onSaveToGroup={handleSaveToGroup}
              savedPointIds={savedPointIds}
              setSavedPointIds={setSavedPointIds}
              onClose={() => {
                setShowSavePopup(false);
              }}
            />
          )}
        </div>

        <button
          className={styles.selectedScreensPopupDownloadButton}
          onClick={handleDownloadClick}
          disabled={isDownloading}
        >
          <img src="/img/blackIcon/Download.svg" alt="Download Button Icon" />
          <p className={styles.selectedScreensPopupDownloadButtonText}>
            {isDownloading ? "Downloading..." : "Download"}
          </p>
          {!organization?.is_pro && (
            <div className={styles.selectedScreensPopupDownloadButtonChip}>
              <p>Pro</p>
            </div>
          )}
        </button>
      </div>

      {showLimitModal && (
        <LimitModal
          handleCloseModal={handleCloseModal}
          maxScreens={MAX_SCREENS}
        />
      )}
    </>
  );
};

export default SelectedScreensPopup;
